import { type Models as M, Errors as E } from "@lux-ai/api-interface"
import type { LuxAiApi } from "@lux-ai/api-interface"
import { getContext, setContext } from "svelte"
import { ProgressController } from "../ProgressHandler.svelte"
import type { SnapshotData } from "../../types"

type View = "themeGeneration"
// "previewGeneration" | "previewSelection"

const GENERATE_CONTROLLER_CONTEXT_KEY = "gen-theme-controller"

export function setGenerateController(ctrl: GenerateThemeController) {
	setContext(GENERATE_CONTROLLER_CONTEXT_KEY, ctrl)
}

export function getGenerateController() {
	return getContext<GenerateThemeController>(GENERATE_CONTROLLER_CONTEXT_KEY)
}

type GenCallbacks = {
	goBack: () => void
	onGenerationStart: (gen: M.ThemeGeneration) => void
	/** Called when the generation is complete - Should reload any required data to be able to call viewScene. */
	onGenerationComplete: (sceneId: number) => void
	onError: (errorMessage: string) => void
}

export class GenerateThemeController {
	api: LuxAiApi
	view: View = $state("themeGeneration")

	readonly selectedTheme: M.Theme
	readonly selectedSnapshot: SnapshotData

	generation: M.ThemeGeneration = $state()
	generationType: M.GenerationType

	callbacks: GenCallbacks

	onGenerationStart: (gen: M.ThemeGeneration) => void
	onGenerationComplete: (sceneId: number) => void
	onError: (errorMessage: string) => void

	// the generated scene
	preview: string = $state()
	progress: number = $state(0)

	progressController: ProgressController

	constructor(
		api: LuxAiApi,
		selection: {
			theme: M.Theme
			snapshot: SnapshotData
			generation?: M.ThemeGeneration
		},
		callbacks: GenCallbacks,
		progressController: ProgressController,
	) {
		this.api = api

		this.selectedTheme = selection.theme
		this.selectedSnapshot = selection.snapshot

		this.onGenerationStart = callbacks.onGenerationStart
		this.onGenerationComplete = callbacks.onGenerationComplete
		this.onError = callbacks.onError

		this.callbacks = callbacks

		this.progressController = progressController

		// Called on mount - begins the theme generation

		if (selection.generation) {
			this.viewGeneration(selection.generation)
		} else {
			this.generateTheme()
		}
	}

	// #region Interface

	/**
	 * Set the specified preivew as the "selected" preview for this theme generation.
	 * Begins the theme generation using this preview as the starting point.
	 */
	approvePreview = () => {
		return this.generateTheme()
	}

	denyPreview = () => {
		return this.goBack()
	}

	goBack = () => {
		this.callbacks.goBack()
		if (this.generation && this.generation.id) {
			this.progressController.clearProgressHandler(this.generation.id)
		}
	}

	// #endregion
	// #region Preview generation
	// see bottom of file
	// #endregion
	// #region Theme generation

	generateTheme = async () => {
		this.progress = 0

		try {
			const generation = await this.api.generateTheme({
				snapshot_id: String(this.selectedSnapshot.id),
				theme_id: this.selectedTheme.id,
				type: "theme",
			})

			const gen = await this.api.fetchGeneration(generation.id)
			this.onGenerationStart(gen)

			this.viewGeneration(generation)
		} catch (e) {
			console.error(e)

			if (e instanceof E.LuxAiRequestError) this.onError(e.message)
			else
				this.onError(
					"An error occurred while trying to generate this theme. Please refresh and try again...",
				)

			this.goBack()
		}
	}

	viewGeneration = (generation: M.ThemeGeneration) => {
		const getProgress = async () => {
			const data = await this.api.fetchGenerationProgress(generation.id)

			return data.progress
		}

		this.progressController.createProgressHandler(
			generation.id,
			getProgress,
			this.onThemeGenerationComplete,
		)

		this.generation = generation
		this.view = "themeGeneration"
	}

	private onThemeGenerationComplete = async () => {
		const sceneId = Number((await this.api.fetchGeneration(this.generation.id)).scene_id)

		this.goBack()
		this.onGenerationComplete(sceneId)
	}
}

// Unused preview logic
/**
 * Trigger a preview generation and listen to the progress.
 */
// private async generatePreview() {
// 	this.progress = 0
// 	const generation = await this.api.generateTheme({
// 		theme_id: this.selectedTheme.id,
// 		snapshot_id: String(this.selectedSnapshot.id),
// 		type: "preview",
// 	})

// 	this.generation = generation
// }

// private async onPreviewGenerationComplete() {
// 	await this.getGeneratedPreviews()
// 	this.view = "previewSelection"
// }

/**
 * Fetches the generated previews (called from the progress handler when done)
 */
// private async getGeneratedPreviews() {
// 	const refreshedGeneration = await this.api.fetchGeneration(this.generation.id)
// 	this.preview = refreshedGeneration.output
// }
